/**
 * Einstellungen (nur Admin).
 *
 * Vier Abschnitte: Taktung des Nachfassens, Textvorlage der automatischen
 * 1. Erinnerung samt Vorschau, Signaturen und die Zugangsdaten der Integrationen.
 *
 * Eine zweite Erinnerungsmail gibt es nicht mehr – die zweite Schwelle legt
 * fest, ab wann der dokumentierte Anruf fällig wird.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal } from '../komponenten/Modal';
import type { EinstellungenStand, NummernHerkunft, Signatur, VorlagenVorschau } from '../typen';
import './einstellungen.css';

export function EinstellungenModal() {
  const { modalSchliessen, toastZeigen } = useKora();
  const meta = useMeta();
  const queryClient = useQueryClient();

  const stand = useQuery({
    queryKey: ['einstellungen'],
    queryFn: () => api.hole<EinstellungenStand>('/einstellungen'),
  });

  const [vorlKat, setVorlKat] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [vorlageGeladen, setVorlageGeladen] = useState('');

  const vorschau = useQuery({
    queryKey: ['vorlagen-vorschau', vorlKat],
    queryFn: () =>
      api.hole<VorlagenVorschau>(
        `/einstellungen/vorschau${vorlKat ? `?prefix=${encodeURIComponent(vorlKat)}` : ''}`,
      ),
  });

  // Vorlagenfelder auf die gewählte Kategorie umstellen.
  useEffect(() => {
    if (!vorschau.data) return;
    const schluessel = vorlKat || '__global__';
    if (vorlageGeladen === schluessel) return;
    setBetreff(vorschau.data.vorlage.betreff);
    setText(vorschau.data.vorlage.text);
    setVorlageGeladen(schluessel);
  }, [vorschau.data, vorlKat, vorlageGeladen]);

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['einstellungen'] });
    void queryClient.invalidateQueries({ queryKey: ['vorlagen-vorschau'] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  const global = useMutation({
    mutationFn: (daten: Partial<EinstellungenStand>) => api.aendere('/einstellungen', daten),
    onSuccess: neuLaden,
  });

  const kategorie = useMutation({
    mutationFn: ({
      prefix,
      ...daten
    }: {
      prefix: string;
      warn?: number;
      krit?: number;
      aktiv?: boolean;
      aufschlag?: number;
    }) =>
      api.ersetze(`/einstellungen/kategorie/${prefix}`, daten),
    onSuccess: neuLaden,
  });

  const vorlageSpeichern = useMutation({
    mutationFn: () =>
      vorlKat
        ? api.ersetze(`/einstellungen/vorlage/${vorlKat}`, { betreff1: betreff, text1: text })
        : api.aendere('/einstellungen', { betreff1: betreff, text1: text }),
    onSuccess: () => {
      neuLaden();
      toastZeigen('Textvorlage gespeichert.');
    },
  });

  const vorlageZuruecksetzen = useMutation({
    mutationFn: () => api.loesche(`/einstellungen/vorlage/${vorlKat}`),
    onSuccess: () => {
      setVorlageGeladen('');
      neuLaden();
      toastZeigen('Eigene Vorlage entfernt – die Kategorie erbt wieder die Standardvorlage.');
    },
  });

  const d = stand.data;

  return (
    <Modal
      titel="Einstellungen"
      untertitel="Erinnerungen, Textvorlagen, Signaturen und Integrationen · nur Admin"
      breite={880}
      abstandOben={40}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      fuss={
        <div className="ein-fuss">
          <div className="ein-fuss-hinweis">
            Die 1. Erinnerung geht automatisch raus – niemand liest gegen. Änderungen an der Vorlage
            gelten ab dem nächsten Lauf.
          </div>
          <button type="button" className="ein-fertig" onClick={modalSchliessen}>
            Fertig
          </button>
        </div>
      }
    >
      {!d && <div className="modal-hinweis">Einstellungen werden geladen …</div>}

      {d && (
        <>
          {/* ---------- Taktung ---------- */}
          <section className="ein-abschnitt">
            <div className="ein-titel">Globale Taktung</div>

            <div className="ein-raster-2">
              <label className="ein-feld">
                <span>1. Erinnerung nach … Tagen</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.warn}
                  min={1}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w > 0 && w !== d.warn) global.mutate({ warn: w });
                  }}
                />
              </label>
              <label className="ein-feld">
                <span>Anruf fällig nach … Tagen</span>
                <input
                  type="number"
                  className="ein-eingabe ein-zahl"
                  defaultValue={d.krit}
                  min={1}
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (w > 0 && w !== d.krit) global.mutate({ krit: w });
                  }}
                />
              </label>
            </div>

            <div className="ein-hinweis">
              Gezählt ab dem Monday-Eintrag „Gesendet". Dieselbe Schwelle färbt die Ampel im Cockpit.
            </div>

            <div className="ein-schalterzeile">
              <div>Eigene Taktung pro Kategorie</div>
              <button
                type="button"
                className={`ein-schalter ${d.proKategorie ? 'an' : ''}`}
                onClick={() => global.mutate({ proKategorie: !d.proKategorie })}
              >
                <span />
              </button>
            </div>

            {d.proKategorie && (
              <div className="ein-kattabelle">
                <div className="ein-katzeile ein-katkopf">
                  <div>Kategorie</div>
                  <div>1. Erinnerung</div>
                  <div>Anruf ab</div>
                  <div>Offen</div>
                  <div style={{ textAlign: 'right' }}>Aktiv</div>
                </div>
                {d.kategorien.map((c) => (
                  <div
                    className="ein-katzeile"
                    key={c.prefix}
                    style={{ opacity: c.aktiv ? 1 : 0.55 }}
                  >
                    <div className="ein-katname">
                      <span className="ein-katprefix">{c.prefix}</span>
                      <span>{c.label}</span>
                    </div>
                    <input
                      type="number"
                      className="ein-katfeld ein-zahl"
                      defaultValue={c.warn}
                      min={1}
                      onBlur={(e) => {
                        const w = Number(e.target.value);
                        if (w > 0 && w !== c.warn) kategorie.mutate({ prefix: c.prefix, warn: w });
                      }}
                    />
                    <input
                      type="number"
                      className="ein-katfeld ein-zahl"
                      defaultValue={c.krit}
                      min={1}
                      onBlur={(e) => {
                        const w = Number(e.target.value);
                        if (w > 0 && w !== c.krit) kategorie.mutate({ prefix: c.prefix, krit: w });
                      }}
                    />
                    <div className="ein-katanzahl">{c.anzahl}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className={`ein-schalter klein ${c.aktiv ? 'an' : ''}`}
                        onClick={() => kategorie.mutate({ prefix: c.prefix, aktiv: !c.aktiv })}
                      >
                        <span />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="ein-gruenblock">
              <span className="ein-gruenicon">↻</span>
              <div>
                <strong>1. Erinnerung: wird automatisch im Namen des Versenders versendet.</strong>{' '}
                Kein Review, kein manueller Klick – die Vorlage unten muss deshalb einmal sorgfältig
                freigegeben werden. Danach folgt kein zweites Mail, sondern der dokumentierte Anruf.
                {meta.trockenlauf && (
                  <>
                    {' '}
                    Aktuell läuft der Testbetrieb: Mails werden nur als Entwurf abgelegt, nicht
                    versendet.
                  </>
                )}
              </div>
            </div>

            <div className="ein-schalterzeile">
              <div className="ein-schalterzeile-text">
                <div>Auto-Erinnerungen pausieren</div>
                <div className="ein-hinweis">
                  {d.pause
                    ? 'Aktuell werden keine Erinnerungen versendet und keine Anrufe fällig gemeldet'
                    : 'Erinnerungen laufen nach Plan'}
                </div>
              </div>
              <button
                type="button"
                className={`ein-schalter ${d.pause ? 'an' : ''}`}
                onClick={() => global.mutate({ pause: !d.pause })}
              >
                <span />
              </button>
            </div>
          </section>

          {/* ---------- Kalkulationsaufschläge ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Kalkulationsaufschläge</div>

            <div className="ein-hinweis">
              Grundlage der Gewinnprognose: Aus einem erteilten Auftrag wird nicht sein voller Wert
              zum Gewinn, sondern der Deckungsbeitrag. Ohne hinterlegten Aufschlag rechnet KORA für
              die Kategorie mit null – lieber vorsichtig als erfunden.
            </div>

            <label className="ein-feldzeile">
              <span>Standardaufschlag, wenn für eine Kategorie keiner hinterlegt ist</span>
              <span className="ein-eingabezeile">
                <input
                  type="number"
                  className="ein-katfeld ein-zahl"
                  defaultValue={d.aufschlagStandard || ''}
                  min={0}
                  max={500}
                  placeholder="0"
                  onBlur={(e) => {
                    const w = Number(e.target.value) || 0;
                    if (w !== d.aufschlagStandard) global.mutate({ aufschlagStandard: w });
                  }}
                />
                <span className="ein-einheit">
                  % Aufschlag
                  {d.margeStandardText ? ` · entspricht ${d.margeStandardText} Marge` : ''}
                </span>
              </span>
            </label>

            <div className="ein-kattabelle">
              <div className="ein-katzeile ein-katkopf ein-margenzeile">
                <div>Kategorie</div>
                <div>Aufschlag</div>
                <div>Marge</div>
              </div>
              {d.kategorien.map((c) => (
                <div className="ein-katzeile ein-margenzeile" key={c.prefix}>
                  <div className="ein-katname">
                    <span className="ein-katprefix">{c.prefix}</span>
                    <span>{c.label}</span>
                  </div>
                  <input
                    type="number"
                    className="ein-katfeld ein-zahl"
                    defaultValue={c.aufschlag || ''}
                    min={0}
                    max={500}
                    placeholder={d.aufschlagStandard ? String(d.aufschlagStandard) : '—'}
                    onBlur={(e) => {
                      const w = Number(e.target.value) || 0;
                      if (w !== c.aufschlag) kategorie.mutate({ prefix: c.prefix, aufschlag: w });
                    }}
                  />
                  <div className="ein-katanzahl">
                    {c.margeText || (d.margeStandardText ? `(${d.margeStandardText})` : '—')}
                  </div>
                </div>
              ))}
            </div>

            <div className="ein-hinweis">
              <strong>Aufschlag ist nicht Marge.</strong> 30 % Aufschlag auf den Einkauf ergeben
              23,1 % Marge vom Verkaufspreis – aus 100 € Einkauf werden 130 € Verkauf mit 30 €
              Deckungsbeitrag. KORA rechnet das um; eingetragen wird der Aufschlag, weil so
              kalkuliert wird.
            </div>
          </section>

          {/* ---------- Textvorlage ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Textvorlage · 1. Erinnerung</div>

            <div className="ein-warnblock">
              <span className="ein-warnicon">!</span>
              <div>
                Wird automatisch im Namen des Versenders versendet – niemand liest gegen. Text bitte
                vor der Freigabe prüfen.
              </div>
            </div>

            <div className="ein-vorlagenkopf">
              <label className="ein-feld">
                <span>Gilt für</span>
                <select
                  className="ein-eingabe ein-breit"
                  value={vorlKat}
                  onChange={(e) => {
                    setVorlKat(e.target.value);
                    setVorlageGeladen('');
                  }}
                >
                  <option value="">Alle Kategorien (Standardvorlage)</option>
                  {meta.kategorien.map((c) => (
                    <option key={c.prefix} value={c.prefix}>
                      {c.prefix} · {c.label}
                    </option>
                  ))}
                </select>
              </label>

              {vorlKat && d.katVorlagen[vorlKat] && (
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => vorlageZuruecksetzen.mutate()}
                >
                  Eigene Vorlage entfernen
                </button>
              )}

              <div className="ein-herkunft">
                {!vorlKat
                  ? 'Gilt für alle Kategorien ohne eigene Vorlage.'
                  : d.katVorlagen[vorlKat]
                    ? 'Eigene Vorlage für diese Kategorie.'
                    : 'Erbt aktuell die Standardvorlage – Speichern legt eine eigene an.'}
              </div>
            </div>

            <label className="ein-feld">
              <span>Betreff</span>
              <input
                className="ein-eingabe"
                value={betreff}
                onChange={(e) => setBetreff(e.target.value)}
              />
            </label>

            <label className="ein-feld">
              <span>Text</span>
              <textarea
                className="ein-eingabe ein-textarea"
                rows={9}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>

            <div className="ein-platzhalter">
              <span className="ein-hinweis">Platzhalter:</span>
              {meta.platzhalter.map((p) => (
                <span className="ein-platz" key={p}>
                  {p}
                </span>
              ))}
            </div>

            <div className="ein-vorlagen-aktionen">
              <button
                type="button"
                className="ein-knopf-prim"
                disabled={vorlageSpeichern.isPending}
                onClick={() => vorlageSpeichern.mutate()}
              >
                {vorlageSpeichern.isPending ? 'Wird gespeichert …' : 'Vorlage speichern'}
              </button>
            </div>

            <div className="ein-vorschau">
              <div className="ein-vorschau-kopf">
                <div className="ein-vorschau-titel">So sieht die Mail aus</div>
                <div className="ein-vorschau-bezug">
                  {vorschau.data?.leer ? 'Noch kein Angebot' : `Beispieldaten · ${vorschau.data?.bezug ?? ''}`}
                </div>
              </div>

              {vorschau.data?.leer ? (
                <div className="ein-vorschau-blatt">
                  <span className="ein-hinweis">{vorschau.data.hinweis}</span>
                </div>
              ) : (
                vorschau.data && (
                  <div className="ein-vorschau-blatt">
                    <div className="mail-kopfzeile">
                      <span>Von</span>
                      <span>{vorschau.data.von}</span>
                    </div>
                    <div className="mail-kopfzeile">
                      <span>An</span>
                      <span>{vorschau.data.an}</span>
                    </div>
                    <div className="mail-betreff">{vorschau.data.betreff}</div>
                    <div className="mail-text">{vorschau.data.text}</div>
                    <div className="mail-signatur">
                      {vorschau.data.signaturHtml ? (
                        <div dangerouslySetInnerHTML={{ __html: vorschau.data.signaturHtml }} />
                      ) : (
                        <span className="mail-signatur-leer">
                          Keine Signatur für {vorschau.data.versender ?? '—'} hinterlegt.
                        </span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </section>

          <Nummernkreis />

          <Signaturen />

          {/* ---------- Anmeldung ---------- */}
          <section className="ein-abschnitt ein-trenner">
            <div className="ein-titel">Anmeldung</div>
            <div className="eh-banner eh-banner--blau">
              {meta.demoModus ? (
                <span>
                  Kora läuft im Demo-Modus: die Personenauswahl meldet ohne Prüfung an. Für den
                  Echtbetrieb in der Konfiguration <code>AUTH_MODE=entra</code> setzen – dann läuft
                  die Anmeldung über das Microsoft-Konto von Eichenherz.
                </span>
              ) : (
                <span>
                  Die Anmeldung läuft über Microsoft Entra ID. Kora speichert kein eigenes Passwort –
                  Passwörter, Zwei-Faktor und Sperren verwaltet Microsoft. Wer Zugang bekommt, legt
                  die Administration im Admin Center fest.
                </span>
              )}
            </div>
          </section>

          <Integrationen daten={d} />
        </>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Nummernkreis der Angebote.
 *
 * Im Regelfall ermittelt Kora die höchste vergebene Nummer selbst – aus dem
 * eigenen Bestand und aus den SharePoint-Ordnern „Angebote" und „Aufträge".
 * Die manuelle Eingabe ist der Notnagel, falls SharePoint nicht erreichbar ist
 * oder der Bestand woanders liegt.
 */
function Nummernkreis() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [eingabe, setEingabe] = useState('');
  const [fehler, setFehler] = useState('');
  const [bearbeiten, setBearbeiten] = useState(false);

  const abfrage = useQuery({
    queryKey: ['nummernkreis'],
    queryFn: () => api.hole<NummernHerkunft>('/einstellungen/nummernkreis'),
  });

  const speichern = useMutation({
    mutationFn: (wert: number | null) =>
      api.ersetze<NummernHerkunft>('/einstellungen/nummernkreis', { letzteNummer: wert }),
    onSuccess: (neu) => {
      queryClient.setQueryData(['nummernkreis'], neu);
      void queryClient.invalidateQueries({ queryKey: ['naechste-angebotsnummer'] });
      setBearbeiten(false);
      setEingabe('');
      setFehler('');
      toastZeigen(`Nächste Angebotsnummer: ${neu.naechste}`);
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  const d = abfrage.data;

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">Nummernkreis der Angebote</div>

      {abfrage.isLoading && <div className="modal-hinweis">Wird ermittelt …</div>}
      {abfrage.isError && (
        <div className="eh-banner eh-banner--gelb">
          {abfrage.error instanceof Error ? abfrage.error.message : 'Nicht ermittelbar.'}
        </div>
      )}

      {d && (
        <>
          <div className="ein-nummer-gross">
            <span className="ein-nummer-wert">{d.naechste}</span>
            <span className="ein-nummer-text">
              bekommt das nächste Angebot · Format JJNNN, Zähler startet jedes Jahr neu bei 001
            </span>
          </div>

          <div className="ein-nummer-quellen">
            <Quelle label="Bestand in Kora" wert={d.ausDatenbank} />
            <Quelle
              label="SharePoint-Ordner"
              wert={d.ausSharepoint ?? d.ausZwischenspeicher}
              zusatz={
                d.sharepointGelesen
                  ? 'Angebote und Aufträge, soeben gelesen'
                  : 'zuletzt erkannter Stand'
              }
            />
            <Quelle label="Manuell gesetzt" wert={d.ausEinstellung} />
          </div>

          {d.hinweis && <div className="eh-banner eh-banner--gelb">{d.hinweis}</div>}

          {!bearbeiten ? (
            <div className="ein-vorlagen-aktionen">
              <button type="button" className="ein-knopf-sek" onClick={() => setBearbeiten(true)}>
                Zähler von Hand setzen
              </button>
              {d.ausEinstellung !== null && (
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => speichern.mutate(null)}
                >
                  Manuelle Vorgabe entfernen
                </button>
              )}
            </div>
          ) : (
            <>
              <label className="ein-feld">
                <span>Zuletzt vergebene Angebotsnummer</span>
                <input
                  className="ein-eingabe ein-zahl"
                  inputMode="numeric"
                  placeholder={String(d.naechste - 1)}
                  value={eingabe}
                  onChange={(e) => {
                    setEingabe(e.target.value.replace(/[^0-9]/g, '').slice(0, 5));
                    setFehler('');
                  }}
                />
                <span className="ein-hinweis">
                  Kora zählt ab hier weiter. Der Wert wirkt nur als Untergrenze – wird in SharePoint
                  eine höhere Nummer gefunden, gilt diese.
                </span>
              </label>
              <div className="ein-vorlagen-aktionen">
                <button
                  type="button"
                  className="ein-knopf-prim"
                  disabled={eingabe.length !== 5 || speichern.isPending}
                  onClick={() => speichern.mutate(Number(eingabe))}
                >
                  {speichern.isPending ? 'Wird gesetzt …' : 'Übernehmen'}
                </button>
                <button
                  type="button"
                  className="ein-knopf-sek"
                  onClick={() => {
                    setBearbeiten(false);
                    setEingabe('');
                    setFehler('');
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </>
          )}

          {fehler && <div className="modal-fehler">{fehler}</div>}
        </>
      )}
    </section>
  );
}

function Quelle({
  label,
  wert,
  zusatz,
}: {
  label: string;
  wert: number | null;
  zusatz?: string;
}) {
  return (
    <div className="ein-quelle">
      <span className="ein-quelle-label">{label}</span>
      <span className="ein-quelle-wert">{wert ?? '—'}</span>
      {zusatz && wert !== null && <span className="ein-quelle-zusatz">{zusatz}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function Signaturen() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [person, setPerson] = useState<string | null>(null);
  const [html, setHtml] = useState('');
  const [geladen, setGeladen] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['signaturen'],
    queryFn: () => api.hole<Signatur[]>('/einstellungen/signaturen'),
  });

  const liste = abfrage.data ?? [];
  const aktuell = person ?? liste[0]?.name ?? null;

  useEffect(() => {
    if (!aktuell || geladen === aktuell) return;
    const s = liste.find((x) => x.name === aktuell);
    if (!s) return;
    setHtml(s.html);
    setGeladen(aktuell);
  }, [aktuell, geladen, liste]);

  const speichern = useMutation({
    mutationFn: () => api.ersetze(`/einstellungen/signaturen/${aktuell}`, { html }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signaturen'] });
      void queryClient.invalidateQueries({ queryKey: ['vorlagen-vorschau'] });
      toastZeigen(`Signatur von ${aktuell} gespeichert.`);
    },
  });

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">Signaturen</div>
      <div className="ein-hinweis">
        HTML-Signatur pro Person. Unter der Mail steht die Signatur des <strong>Versenders</strong> –
        das ist nicht zwingend der Kalkulant.
      </div>

      <div className="ein-signaturen">
        <div className="ein-sigliste">
          {liste.map((p) => (
            <button
              key={p.name}
              type="button"
              className={`ein-sigperson ${aktuell === p.name ? 'aktiv' : ''}`}
              onClick={() => {
                setPerson(p.name);
                setGeladen(null);
              }}
            >
              <span className="eh-avatar ein-sigavatar">
                {p.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="ein-sigtext">
                <span className="ein-signame">{p.name}</span>
                <span className="ein-sigstatus">
                  {p.hinterlegt ? 'Signatur hinterlegt' : 'keine Signatur'}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="ein-sigeditor">
          <label className="ein-feld">
            <span>HTML-Signatur · {aktuell ?? '—'}</span>
            <textarea
              className="ein-eingabe ein-code"
              rows={7}
              value={html}
              placeholder="<table>…</table> oder <div>…</div>"
              onChange={(e) => setHtml(e.target.value)}
            />
          </label>

          <div className="ein-sigaktionen">
            <label className="ein-knopf-sek">
              HTML-Datei hochladen
              <input
                type="file"
                accept=".html,.htm,text/html"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const datei = e.target.files?.[0];
                  if (!datei) return;
                  const leser = new FileReader();
                  leser.onload = () => setHtml(String(leser.result));
                  leser.readAsText(datei);
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" className="ein-knopf-sek" onClick={() => setHtml('')}>
              Signatur entfernen
            </button>
            <button
              type="button"
              className="ein-knopf-prim"
              disabled={!aktuell || speichern.isPending}
              onClick={() => speichern.mutate()}
            >
              {speichern.isPending ? 'Wird gespeichert …' : 'Signatur speichern'}
            </button>
          </div>

          <div className="ein-sigvorschau">
            <div className="ein-vorschau-titel">Vorschau</div>
            <div className="ein-sigvorschau-blatt">
              {html ? (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <span className="mail-signatur-leer">Noch keine Signatur hinterlegt.</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

function Integrationen({ daten }: { daten: EinstellungenStand }) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [token, setToken] = useState('');
  const [boardId, setBoardId] = useState(daten.integrationen.mondayBoardId);
  const [siteId, setSiteId] = useState(daten.integrationen.sharepointSiteId);
  const [driveId, setDriveId] = useState(daten.integrationen.sharepointDriveId);

  const speichern = useMutation({
    mutationFn: () =>
      api.aendere('/einstellungen/integrationen', {
        ...(token ? { mondayApiToken: token } : {}),
        mondayBoardId: boardId,
        sharepointSiteId: siteId,
        sharepointDriveId: driveId,
      }),
    onSuccess: () => {
      setToken('');
      void queryClient.invalidateQueries({ queryKey: ['einstellungen'] });
      toastZeigen('Integrations-Einstellungen gespeichert.');
    },
  });

  return (
    <section className="ein-abschnitt ein-trenner">
      <div className="ein-titel">Monday und SharePoint</div>
      <div className="ein-hinweis">
        Zugangsdaten gehören bevorzugt in die <code>.env</code>-Datei des Servers. Hier eingetragene
        Werte haben Vorrang und werden verschlüsselt gespeichert – der Token wird nie wieder
        angezeigt.
      </div>

      <div className="ein-statuszeile">
        <span className={`ein-status ${daten.integrationen.mondayAktiv ? 'an' : ''}`}>
          Monday {daten.integrationen.mondayAktiv ? 'aktiv' : 'nicht aktiv'}
        </span>
        <span className={`ein-status ${daten.integrationen.graphAktiv ? 'an' : ''}`}>
          Microsoft Graph {daten.integrationen.graphAktiv ? 'aktiv' : 'nicht aktiv'}
        </span>
      </div>

      <div className="ein-raster-2">
        <label className="ein-feld">
          <span>
            Monday API-Token{' '}
            {daten.integrationen.mondayTokenHinterlegt && (
              <span className="ein-hinterlegt">hinterlegt</span>
            )}
          </span>
          <input
            type="password"
            className="ein-eingabe"
            value={token}
            placeholder={
              daten.integrationen.mondayAusUmgebung
                ? 'aus .env übernommen – hier überschreibbar'
                : 'Token einfügen'
            }
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <label className="ein-feld">
          <span>Monday Board-ID</span>
          <input
            className="ein-eingabe ein-zahl"
            value={boardId}
            onChange={(e) => setBoardId(e.target.value)}
          />
        </label>
        <label className="ein-feld">
          <span>SharePoint Site-ID</span>
          <input
            className="ein-eingabe"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
          />
        </label>
        <label className="ein-feld">
          <span>SharePoint Drive-ID</span>
          <input
            className="ein-eingabe"
            value={driveId}
            onChange={(e) => setDriveId(e.target.value)}
          />
        </label>
      </div>

      <div className="ein-vorlagen-aktionen">
        <button
          type="button"
          className="ein-knopf-prim"
          disabled={speichern.isPending}
          onClick={() => speichern.mutate()}
        >
          {speichern.isPending ? 'Wird gespeichert …' : 'Zugangsdaten speichern'}
        </button>
      </div>
    </section>
  );
}
